import { useState, useCallback, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "../lib/supabase";
import { useGoogleCalendar } from "../contexts/useGoogleCalendar";
import { CalendarEvent } from "../types";

interface UseCalendarEventsReturn {
  events: CalendarEvent[];
  isLoading: boolean;
  error: string | null;
  isAuthExpired: boolean;
  fetchEvents: (start: string, end: string) => Promise<void>;
  updateExternalEvent: (
    calendarId: string,
    eventId: string,
    start: string,
    end: string,
    allDay: boolean,
  ) => Promise<boolean>;
  createStandaloneEvent: (params: {
    title: string;
    description?: string;
    date: string;
    time: string | null;
    duration: number | null;
    timezone: string;
    attendees?: string[];
  }) => Promise<boolean>;
}

export function useCalendarEvents(): UseCalendarEventsReturn {
  const { connection } = useGoogleCalendar();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthExpired, setIsAuthExpired] = useState(false);
  // Cache keyed by `${start}_${end}`. The same key set is also the source
  // of truth for "which ranges has the user loaded so far", which is what
  // the visibility-change and create-event paths refetch.
  const cacheRef = useRef<Map<string, CalendarEvent[]>>(new Map());

  // Merge new events into current state, deduplicating by calendarId+id
  const mergeEvents = useCallback(
    (incoming: CalendarEvent[]) => {
      setEvents((prev) => {
        const keyOf = (e: CalendarEvent) => `${e.calendarId}::${e.id}`;
        const merged = new Map(prev.map((e) => [keyOf(e), e]));
        for (const e of incoming) merged.set(keyOf(e), e);
        return Array.from(merged.values());
      });
    },
    [],
  );

  const fetchEventsFromServer = useCallback(
    async (start: string, end: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          "google-calendar-events",
          { body: { action: "read", start, end } },
        );

        if (fnError) {
          throw new Error(
            fnError.message || "Failed to fetch calendar events",
          );
        }

        if (data?.error === "GOOGLE_TOKEN_EXPIRED") {
          setIsAuthExpired(true);
          setEvents([]);
          return;
        }

        setIsAuthExpired(false);
        const fetchedEvents: CalendarEvent[] = data?.events || [];
        const cacheKey = `${start}_${end}`;
        cacheRef.current.set(cacheKey, fetchedEvents);
        mergeEvents(fetchedEvents);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [mergeEvents],
  );

  const fetchEvents = useCallback(
    async (start: string, end: string) => {
      const cacheKey = `${start}_${end}`;

      // Already fetched this range — no-op (events are already merged in state)
      if (cacheRef.current.has(cacheKey)) {
        return;
      }

      await fetchEventsFromServer(start, end);
    },
    [fetchEventsFromServer],
  );

  // Refetch every range the user has loaded so far. Used by paths that
  // need to surface a server-side change (visibility toggle, event create)
  // across whatever weeks the user has already navigated to — including
  // prefetched neighbors of the visible week.
  //
  // We fetch all ranges in parallel *without touching state*, then swap
  // cache + events atomically. Clearing first would make the calendar
  // flash empty for a beat before the new data arrives.
  const refetchAllLoadedRanges = useCallback(async () => {
    const loadedRanges: Array<{ start: string; end: string }> = [];
    for (const key of cacheRef.current.keys()) {
      const sep = key.indexOf("_");
      if (sep > 0) {
        loadedRanges.push({ start: key.slice(0, sep), end: key.slice(sep + 1) });
      }
    }
    if (loadedRanges.length === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const responses = await Promise.all(
        loadedRanges.map(async (r) => {
          const { data, error: fnError } = await supabase.functions.invoke(
            "google-calendar-events",
            { body: { action: "read", start: r.start, end: r.end } },
          );
          if (fnError) {
            throw new Error(
              fnError.message || "Failed to fetch calendar events",
            );
          }
          return { range: r, data };
        }),
      );

      if (responses.some((r) => r.data?.error === "GOOGLE_TOKEN_EXPIRED")) {
        setIsAuthExpired(true);
        setEvents([]);
        cacheRef.current.clear();
        return;
      }
      setIsAuthExpired(false);

      const newCache = new Map<string, CalendarEvent[]>();
      const allEvents: CalendarEvent[] = [];
      const seen = new Set<string>();
      for (const { range, data } of responses) {
        const rangeEvents: CalendarEvent[] = data?.events || [];
        newCache.set(`${range.start}_${range.end}`, rangeEvents);
        for (const e of rangeEvents) {
          const key = `${e.calendarId}::${e.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          allEvents.push(e);
        }
      }
      cacheRef.current = newCache;
      setEvents(allEvents);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // When calendar visibility changes, refetch every range the user has
  // loaded. We refetch all of them — not just "the last one" — because
  // CalendarView prefetches three ranges per view (prev, current, next),
  // so a single "last range" pointer would land on a neighbor week and
  // leave the visible week empty until the user navigates.
  const hiddenCalendarIds = connection?.hidden_calendar_ids;
  useEffect(() => {
    if (!connection) return;
    refetchAllLoadedRanges();
  }, [hiddenCalendarIds, refetchAllLoadedRanges, connection]);

  // Prefetch the current week's events as soon as a Google Calendar connection
  // is available, so navigating to /calendar shows events instantly instead of
  // waiting for the lazy-loaded view to mount and trigger its first fetch.
  const connectionId = connection?.id;
  const weekStartDay = connection?.week_start_day ?? 0;
  useEffect(() => {
    if (!connectionId) return;

    const today = new Date();
    const daysFromWeekStart = (today.getDay() - weekStartDay + 7) % 7;

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - daysFromWeekStart);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    fetchEventsFromServer(weekStart.toISOString(), weekEnd.toISOString());
  }, [connectionId, weekStartDay, fetchEventsFromServer]);

  const updateExternalEvent = useCallback(
    async (
      calendarId: string,
      eventId: string,
      start: string,
      end: string,
      allDay: boolean,
    ): Promise<boolean> => {
      try {
        const { error: fnError } = await supabase.functions.invoke(
          "google-calendar-events",
          {
            body: {
              action: "update-external",
              calendarId,
              eventId,
              start,
              end,
              allDay,
            },
          },
        );

        if (fnError) {
          throw new Error(fnError.message || "Failed to update event");
        }

        // Invalidate cache so next fetch gets fresh data
        cacheRef.current.clear();
        return true;
      } catch (err) {
        Sentry.captureException(err);
        return false;
      }
    },
    [],
  );

  const createStandaloneEvent = useCallback(
    async (params: {
      title: string;
      description?: string;
      date: string;
      time: string | null;
      duration: number | null;
      timezone: string;
      attendees?: string[];
    }): Promise<boolean> => {
      try {
        const { error: fnError } = await supabase.functions.invoke(
          "google-calendar-events",
          {
            body: {
              action: "create-standalone",
              title: params.title,
              description: params.description,
              date: params.date,
              time: params.time,
              duration: params.duration,
              timezone: params.timezone,
              attendees: params.attendees,
            },
          },
        );

        if (fnError) {
          throw new Error(fnError.message || "Failed to create event");
        }

        await refetchAllLoadedRanges();
        return true;
      } catch (err) {
        Sentry.captureException(err);
        return false;
      }
    },
    [refetchAllLoadedRanges],
  );

  return {
    events,
    isLoading,
    error,
    isAuthExpired,
    fetchEvents,
    updateExternalEvent,
    createStandaloneEvent,
  };
}
